import { motion } from "framer-motion";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";

interface PlatformBreakdownCardProps {
  platforms: string[];
  values: number[];
  colors: string[];
}

export default function PlatformBreakdownCard({
  platforms,
  values,
  colors,
}: PlatformBreakdownCardProps) {
  const total = values.reduce((sum, value) => sum + value, 0);
  const isEmpty = platforms.length === 0 || total === 0;

  const data = platforms.map((label, index) => ({
    id: index,
    label,
    value: values[index] ?? 0,
    color: colors[index] || "#E0E0E0",
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <Card sx={{ borderRadius: 3, boxShadow: 0, height: "100%" }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Platform Breakdown
          </Typography>

          {isEmpty ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 250,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No platform report
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  height: 200,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <PieChart
                  series={[
                    {
                      data,
                      paddingAngle: 5,
                      innerRadius: "60%",
                      outerRadius: "100%",
                      cornerRadius: 25,
                      startAngle: -120,
                      endAngle: 240,
                    },
                  ]}
                  width={200}
                  height={200}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  hideLegend
                  colors={data.map((item) => item.color)}
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                {data.map((item) => {
                  const percent =
                    total > 0 ? Math.round((item.value / total) * 100) : 0;
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: 0.5,
                            backgroundColor: item.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" noWrap>
                          {item.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, flexShrink: 0 }}
                      >
                        {percent}%
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
